import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { formatBytes } from '../../services/SandboxService';
import { cancelZipUpload, completeZipUpload, startZipUpload } from '../../services/MyBackupsService';

/**
 * "Restore from a zip…" (MyBackups.jsx): what a zip may be and what comes back, the file, its upload in chunks with
 * a progress bar and Cancel, then the gateway's check. Once accepted, the zip is one more backup in the list
 * (madeBy 'upload') and `onDone(out)` hands it to the usual restore dialog (typed confirmation, safety backup).
 */

/** What a checked zip is, in one sentence, by the layout the gateway found. */
export function zipLayoutText(zip) {
  switch (zip?.layout) {
    case 'flow': return 'Made by Flow’s Download: its projects still in your sandbox get their files replaced, the others come back.';
    case 'flow-folders': return 'Project folders from an older Flow download: they come in as new projects, nothing existing is replaced.';
    case 'folder': return 'Not made by Flow: its files come in as one new project, nothing existing is replaced.';
    default: return null;
  }
}

// ---- Uploaded zips in the backup list: not backups of the sandbox, so they can be discarded or hidden ----

/** An uploaded zip ("Restore from a zip…"), listed among the backups (`madeBy.kind: 'upload'`). */
export const isUpload = (backup) => backup?.madeBy?.kind === 'upload';

/**
 * Whether the gateway can drop this upload now: one still `uploading` (abandoned mid-way: the tab closed, the network
 * went) always can (`DELETE /uploads/:id`); a checked one only where the gateway says so (`discardable`).
 */
export const canDiscardUpload = (backup) => isUpload(backup) && (backup.status === 'uploading' || backup.discardable === true);

const HIDDEN_KEY = 'flow.backups.hiddenUploads';

const readHidden = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(HIDDEN_KEY) || '[]');
    return Array.isArray(value) ? value.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

/** The uploaded zips this viewer took off the list (remembered in this browser): `[hiddenIds, hide(id)]`. */
export function useHiddenUploads() {
  const [hidden, setHidden] = useState(readHidden);
  const hide = useCallback((id) => {
    setHidden((current) => {
      if (current.includes(id)) { return current; }
      const next = [...current, id].slice(-200);
      try { window.localStorage.setItem(HIDDEN_KEY, JSON.stringify(next)); } catch { /* hidden until a reload */ }
      return next;
    });
  }, []);
  return [hidden, hide];
}

/** The backups to list: a cancelled upload is gone (nothing to restore), a hidden one is off this viewer's list. */
export const listedBackups = (backups = [], hidden = []) => backups.filter((backup) => !(isUpload(backup) && (backup.status === 'cancelled' || hidden.includes(backup.id))));

/**
 * An uploaded zip's own action, next to Restore…: Discard where the gateway can drop it (canDiscardUpload), else
 * "Remove from list" (only this viewer's list; the gateway still keeps it until its backups expire).
 */
export function UploadActions({ backup, discarding = false, onDiscard, onHide }) {
  const name = backup.zip?.filename || backup.name || 'the uploaded zip';
  if (canDiscardUpload(backup)) {
    return (
      <Button aria-label={`Discard ${name}`} loading={discarding} onClick={() => onDiscard(backup)} size="small">
        Discard
      </Button>
    );
  }
  return (
    <Button aria-label={`Remove ${name} from the list`} onClick={() => onHide(backup)} size="small" title="Only from this list: you can upload the zip again">
      Remove from list
    </Button>
  );
}

UploadActions.propTypes = { backup: PropTypes.object.isRequired, discarding: PropTypes.bool, onDiscard: PropTypes.func.isRequired, onHide: PropTypes.func.isRequired };

/** "Replaces your project Shop" / "New project". */
export const zipProjectNote = (project) => (project?.replaces ? 'replaces the project in your sandbox (its history is kept)' : 'comes in as a new project');

export function ZipExplanation({ maxBytes }) {
  return (
    <div className="sbx-bk-hint sbx-bk-zip-help">
      <p>Accepted: a zip made by Flow’s “Download” (your whole sandbox or one project), or a plain zip of one project folder{maxBytes ? `, up to ${formatBytes(maxBytes)}` : ''}.</p>
      <ul>
        <li>Only files come back: no git history, no data or databases, no app secrets.</li>
        <li>A zip this version of Flow made knows its projects: those still in your sandbox get their files replaced (their history is kept, the zip’s files become the current ones); the others come back.</li>
        <li>A zip Flow did not make (or an older download) comes in as new projects: nothing in your sandbox is replaced.</li>
      </ul>
      <p>You check what is in it and confirm before anything is restored.</p>
    </div>
  );
}

ZipExplanation.propTypes = { maxBytes: PropTypes.number };

export function ZipUploadDialogBody({ uploads, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [sent, setSent] = useState(null); // { sent, total } while uploading
  const [phase, setPhase] = useState('choose'); // choose | uploading | checking
  const [error, setError] = useState(null);
  const fileId = useId();
  const uploadId = useRef(null);
  const controller = useRef(null);

  // Closed while it uploads: the upload stops and the gateway drops what it received.
  useEffect(() => () => {
    if (controller.current) { controller.current.abort(); }
    if (uploadId.current) { cancelZipUpload(uploadId.current).catch(() => {}); }
  }, []);

  const tooBig = Boolean(file && uploads?.maxBytes && file.size > uploads.maxBytes);
  const busy = phase !== 'choose';

  const submit = async(event) => {
    event.preventDefault();
    if (!file || tooBig) { return; }
    setError(null);
    setPhase('uploading');
    setSent({ sent: 0, total: file.size });
    controller.current = new AbortController();
    try {
      const id = await startZipUpload(file, {
        signal: controller.current.signal,
        onStart: (created) => { uploadId.current = created; },
        onProgress: setSent,
      });
      setPhase('checking');
      const out = await completeZipUpload(id);
      uploadId.current = null;
      onDone(out);
    } catch (err) {
      if (err.name === 'AbortError') { return; }
      if (uploadId.current) { cancelZipUpload(uploadId.current).catch(() => {}); uploadId.current = null; }
      setError(err.message || 'The zip could not be uploaded.');
      setPhase('choose');
    } finally {
      controller.current = null;
    }
  };

  const cancel = () => {
    if (controller.current) { controller.current.abort(); }
    if (uploadId.current) { cancelZipUpload(uploadId.current).catch(() => {}); uploadId.current = null; }
    setPhase('choose');
    setSent(null);
    onClose();
  };

  const percent = sent && sent.total ? Math.round((sent.sent / sent.total) * 100) : 0;
  return (
    <form noValidate onSubmit={submit}>
      <ZipExplanation maxBytes={uploads?.maxBytes} />
      <label className="sbx-bk-label" htmlFor={fileId}>Zip file</label>
      <input
        accept=".zip,application/zip"
        className="sbx-bk-file"
        disabled={busy}
        id={fileId}
        onChange={(event) => { setFile(event.target.files?.[0] || null); setError(null); }}
        type="file"
      />
      {file && <p className="sbx-bk-hint sbx-bk-small">{`${file.name} · ${formatBytes(file.size)}`}</p>}
      {tooBig && <p className="sbx-bk-error" role="alert">{`This zip is larger than the ${formatBytes(uploads.maxBytes)} your plan allows.`}</p>}
      {phase === 'uploading' && sent && (
        <div className="sbx-bk-progress" role="status">
          <progress aria-label="Upload progress" max={sent.total || 1} value={sent.sent} />
          <span>{`Uploading: ${formatBytes(sent.sent)} of ${formatBytes(sent.total)} (${percent}%)`}</span>
        </div>
      )}
      {phase === 'checking' && <p className="sbx-bk-hint" role="status">Checking the zip…</p>}
      {error && <p className="sbx-bk-error" role="alert">{error}</p>}
      <div className="sbx-bk-actions">
        <Button disabled={phase === 'checking'} onClick={cancel}>Cancel</Button>
        <Button disabled={!file || tooBig || busy} htmlType="submit" loading={busy} type="primary">
          {phase === 'uploading' ? 'Uploading…' : phase === 'checking' ? 'Checking…' : 'Upload and check'}
        </Button>
      </div>
    </form>
  );
}

ZipUploadDialogBody.propTypes = { uploads: PropTypes.object, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };
